import AddIcon from '@mui/icons-material/Add';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { Grid, Link, TextField } from '@mui/material';
import { styled } from '@mui/material/styles';
import { isEmpty } from 'lodash-es';
import React, {
  useState,
  ReactNode,
  useEffect,
  useCallback,
  FC,
  ChangeEvent,
  SyntheticEvent,
} from 'react';

import WsColors from '../../WsColors';
import { Label } from '../Label';

const LinkTag = styled(Link)(() => ({
  color: WsColors.TheBlues500,
  cursor: 'pointer',
}));

interface ISocialLinkInput {
  labelText?: string;
  initialValue: string[];
  onLinkChange(args: string[]): void;
  onBlur?: (args: SyntheticEvent) => void;
}

const SocialLinkInput: FC<ISocialLinkInput> = ({
  initialValue = [],
  onLinkChange,
  labelText = 'Social Link',
  onBlur,
}) => {
  const [linksValue, setLinksValue] = useState<string[]>(Array.from(new Set(initialValue)));
  const [totalLinks, setTotalLinks] = useState<ReactNode[]>([]);

  useEffect(() => {
    setLinksValue(Array.from(new Set(initialValue)));
  }, [initialValue]);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>, index: number) => {
      setLinksValue((linksValue) => {
        linksValue[index] = event.target.value;
        const newValues = [...linksValue];
        onLinkChange(Array.from(new Set(newValues)));
        return newValues;
      });
    },
    [onLinkChange]
  );

  const addSocialLink = () => {
    if (isEmpty(totalLinks)) {
      setTotalLinks([socialLinkInputWithRemoveIcon(0)]);
    } else {
      setTotalLinks([...totalLinks, socialLinkInputWithRemoveIcon(totalLinks.length)]);
    }
  };

  const removeSocialLink = useCallback(
    (index: number) => {
      setTotalLinks((totalLinks) => {
        delete totalLinks[index];
        return [...totalLinks];
      });
      setLinksValue((linksValue) => {
        delete linksValue[index + 1];
        const newValues = [...linksValue];
        onLinkChange(Array.from(new Set(newValues)));
        return newValues;
      });
    },
    [onLinkChange]
  );

  const socialLinkInputWithRemoveIcon = useCallback(
    (index: number) => (
      <Grid item xs={12} key={index}>
        <TextField
          sx={{ mt: '12px', width: '90%' }}
          inputProps={{ style: { padding: '11.5px 14px' } }}
          id="outlined-basic"
          defaultValue={linksValue[index + 1]}
          placeholder="https://www.linkedin.com/in/rebeccabardesign/"
          variant="outlined"
          onBlur={onBlur}
          onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange(e, index + 1)}
          autoComplete="candidate_socialurl"
        />
        <RemoveCircleOutlineIcon
          onClick={() => removeSocialLink(index)}
          sx={{
            color: WsColors.Red400,
            mt: '20px',
            pl: '20px',
            cursor: 'pointer',
            fontSize: '42px',
          }}
        />
      </Grid>
    ),
    [handleChange, linksValue, onBlur, removeSocialLink]
  );

  useEffect(() => {
    if (!isEmpty(linksValue) && isEmpty(totalLinks)) {
      const links: ReactNode[] = [];
      linksValue.forEach((link: string, index: number) => {
        index !== 0 && links.push(socialLinkInputWithRemoveIcon(index - 1));
      });
      if (!isEmpty(links)) {
        setTotalLinks([...links]);
      }
    }
  }, [linksValue, socialLinkInputWithRemoveIcon, totalLinks]);

  return (
    <Grid container direction="row">
      <Grid item xs={12}>
        <Label>{labelText}</Label>
        <TextField
          inputProps={{ style: { padding: '11.5px 14px' } }}
          fullWidth
          value={linksValue[0]}
          onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange(e, 0)}
          id="outlined-basic"
          onBlur={onBlur}
          placeholder="https://www.linkedin.com/in/rebeccabardesign/"
          variant="outlined"
          autoComplete="candidate_socialUrl"
        />
      </Grid>
      {totalLinks.map((component: ReactNode) => component)}
      <Grid item xs={12}>
        <LinkTag underline="none" onClick={() => addSocialLink()}>
          <Grid container direction="row" alignItems="center">
            <Grid item>
              <AddIcon sx={{ height: '20px', mt: '7px' }} />
            </Grid>
            <Grid item>Add More</Grid>
          </Grid>
        </LinkTag>
      </Grid>
    </Grid>
  );
};

export default SocialLinkInput;
